import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ProcessorDetails } from '@/lib/api/processors'
import { ProcessorHeader } from '../components/processor-header'
import { ProcessorOverviewCard } from '../components/processor-overview-card'
import { ProcessorMetricsCard } from '../components/processor-metrics-card'
import { ProcessorPaymentMethodsCard } from '../components/processor-payment-methods-card'
import { unlimitWebhookUrl } from './webhook-url'

type Props = {
  processor: ProcessorDetails
}

const MASKED = '••••••••••••••••'

const readString = (options: Record<string, unknown> | null | undefined, key: string): string | undefined => {
  const value = options?.[key]
  return typeof value === 'string' && value !== '' ? value : undefined
}

const UnlimitConfigurationCard = ({ processor }: Props) => {
  const { t } = useTranslation('processors')
  const options = processor.options
  const terminalCode = readString(options, 'terminalCode')
  const hasCallbackSecret = readString(options, 'webhookSecret') !== undefined
  const isMerchantVault = options?.storedCardMode === 'merchant_vault'
  const isTestMode = options?.testMode === true

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('unlimit.configurationTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">{t('unlimit.credentials.terminalCodeLabel')}</p>
            <p className="font-mono text-sm">
              {terminalCode ?? <span className="italic text-muted-foreground">{t('notConfigured')}</span>}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('unlimit.credentials.passwordLabel')}</p>
            <p className="font-mono text-sm">{MASKED}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('unlimit.credentials.callbackSecretLabel')}</p>
            <Badge variant={hasCallbackSecret ? 'default' : 'outline'}>
              {hasCallbackSecret ? t('configured') : t('notConfigured')}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('unlimit.storedCardMode.label')}</p>
            <p className="text-sm">
              {isMerchantVault ? t('unlimit.storedCardMode.merchantVault') : t('unlimit.storedCardMode.unlimitVault')}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('environment')}</p>
            <Badge variant={isTestMode ? 'outline' : 'default'}>{isTestMode ? t('sandbox') : t('production')}</Badge>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-muted-foreground">{t('webhookUrl')}</p>
            <p className="break-all font-mono text-xs">{unlimitWebhookUrl(processor.id)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const UnlimitProcessorDetailsPage = ({ processor }: Props) => (
  <>
    <ProcessorHeader type="unlimit" processor={processor} />
    <ProcessorOverviewCard processor={processor} />
    <ProcessorMetricsCard processor={processor} />
    <UnlimitConfigurationCard processor={processor} />
    <ProcessorPaymentMethodsCard processor={processor} />
  </>
)
